import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useGetSystemComponent } from 'src/features/systems/api/systemComponents/useGetSystemComponent';
import SystemComponentForm from 'src/features/systems/components/systemComponents/form/SystemComponentForm';
import { systemComponentFormSchema, SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import {
    DataJson,
    SystemComponent,
    TICKET_PRINTER_ROW_TYPE,
    TicketPrinterPayload,
} from 'src/features/systems/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useListGateways } from 'src/features/gateway/api/useListGateways';

interface SystemComponentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSystemComponent?: SystemComponent;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: FormData, onSuccess: () => void, onError: (error: AxiosError<SmartParkError, any>) => void) => void;
}

function SystemComponentFormModal<TData>({
    selectedSystemComponent,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: SystemComponentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();
    const { locale } = useIntl();

    const { data: systemComponent } = useGetSystemComponent({
        componentId: selectedSystemComponent?.id ?? '',
        parkingId,
        config: { enabled: !!selectedSystemComponent?.id && isModalOpen },
    });
    const onError = useOnError();

    const logoTitle = systemComponent?.logo_url;

    const defaultValues = React.useMemo(
        () =>
            systemComponent
                ? {
                      ...systemComponent,
                      zone: systemComponent?.zone?.id || '',
                      gateway: systemComponent?.gateway?.id || '',
                      pay_station: systemComponent.pay_station?.id,
                      data: {
                          ...systemComponent.data,
                          payload:
                              systemComponent.data?.payload?.map((p) => ({
                                  ...p,
                              })) ?? [],
                      },
                      logo: undefined,
                  }
                : {},
        [systemComponent],
    );

    const form = useForm<SystemComponentFormSchemaValues>({
        resolver: zodResolver(systemComponentFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        if (!systemComponent) return;
        form.reset(defaultValues);
    }, [form, defaultValues, isModalOpen, systemComponent]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const cleanPayload = (payload?: TicketPrinterPayload[]) =>
        payload?.map((item: TicketPrinterPayload) => {
            if (item.type !== TICKET_PRINTER_ROW_TYPE.TEXT) {
                const { text, ...rest } = item;
                return rest;
            }
            return item;
        });

    const onSubmit: SubmitHandler<SystemComponentFormSchemaValues> = async (values) => {
        const formData = new FormData();
        const dirtyFields = form.formState.dirtyFields;
        const data = values.data as DataJson;

        const cleanedData = {
            ...values.data,
            payload: cleanPayload(data?.payload),
        };

        (Object.keys(dirtyFields) as (keyof SystemComponentFormSchemaValues)[]).forEach((key) => {
            const value = values[key];

            if (value === undefined || value === null) return;

            if (value instanceof File) {
                formData.append(key, value, value.name);
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    formData.append(`${String(key)}[]`, String(item));
                });
                return;
            }

            if (typeof value === 'object') {
                formData.append('data', JSON.stringify(cleanedData));
                return;
            }

            formData.append(key, String(value));
        });

        mutate(
            formData,
            () => {
                setIsModalOpen(false);
                form.reset();
            },
            (error) => onError(error, form.setError),
        );
    };

    const { data: zoneData } = useListZones({ parkingId });
    const { data: gatewayData } = useListGateways({ parkingId });

    const zoneOptions = React.useMemo(
        () =>
            zoneData?.results?.map((zone) => ({
                label: zone?.translations.find((translation) => translation.language === locale)?.name,
                value: zone.id,
            })),
        [zoneData, locale],
    );

    const gatewayOptions = React.useMemo(
        () =>
            gatewayData?.results?.map((gateway) => ({
                label: gateway?.translations.find((translation) => translation.language === locale)?.name,
                value: gateway.id,
            })),
        [gatewayData, locale],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon}></SquareIcon>
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SystemComponentForm
                        form={form}
                        onSubmit={onSubmit}
                        zoneOptions={zoneOptions}
                        gatewayOptions={gatewayOptions}
                        logoTitle={logoTitle}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedSystemComponent && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SystemComponentFormModal;
